import { Injector, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { timeout, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { getAngularAppInjector } from 'app/angular-app.bootstrap';
import { getLeafInjector } from 'app/angular-leaf.bootstrap';

export const SUPPORTED_LANGUAGES = ['pt_BR', 'en_US', 'es_MX'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const FALLBACK_LANGUAGE: SupportedLanguage = 'pt_BR';

function isSupportedLanguage(value: unknown): value is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly unknown[]).includes(value);
}

/** Retorna um idioma suportado ou o fallback pt_BR. Pura, sem side effects. */
export function resolveSupportedLanguage(raw: string | null | undefined): string {
  return isSupportedLanguage(raw) ? raw : FALLBACK_LANGUAGE;
}

/**
 * Aplica o idioma resolvido a um TranslateService já resolvido do injector Angular
 * corrente. Idioma já ativo é no-op (sem onLangChange). Falha de carregamento
 * (rede/parse/timeout) preserva o idioma anterior e nunca propaga para o React.
 *
 * A aplicação roda dentro do NgZone do injector porque esta função é chamada a
 * partir de um handler de evento do React (troca de idioma na navbar), que executa
 * FORA do NgZone do Angular. Sem re-entrar na zona, tanto a emissão de onLangChange
 * quanto a conclusão assíncrona do carregamento do pacote (HttpClient) ocorreriam
 * fora da zona: o Angular não agendaria change detection e a tela visível ficaria
 * no idioma antigo até um evento in-zone. O zone.run garante a re-tradução imediata.
 */
function applyLanguageToService(translate: TranslateService, resolved: string, zone: NgZone | null): void {
  // Idioma já ativo: evita disparar onLangChange e uma requisição de pacote desnecessária.
  if (translate.currentLang === resolved) {
    return;
  }

  const applyUse = () =>
    translate
      .use(resolved)
      .pipe(
        timeout(10000),
        // Falha de rede/parse/timeout não pode derrubar a tela nem vazar para o React;
        // o idioma ativo permanece o anterior porque use() só muda currentLang em sucesso.
        catchError((err) => {
          console.error(`[language-bridge] falha ao aplicar o idioma "${resolved}" no Angular montado:`, err);
          return of(void 0);
        }),
      )
      .subscribe();

  // Fallback direto quando não há NgZone (não esperado com um injector Angular
  // real): preserva o comportamento atual em vez de regredir.
  if (zone) {
    zone.run(applyUse);
  } else {
    applyUse();
  }
}

/**
 * Aplica no Angular montado o idioma correspondente ao id informado. O Angular vivo
 * pode ser o AppModule completo (telas reais com FullLayout) e/ou a folha isolada;
 * ambos os injectors são resolvidos a cada chamada (sem cache), para nunca atuar
 * sobre uma instância já destruída. Cada TranslateService distinto recebe a troca
 * uma única vez. Sem nenhum Angular montado, é um no-op registrado em log; nunca
 * lança para o React.
 */
export function setAngularLanguage(id: string | null | undefined): void {
  const resolved = resolveSupportedLanguage(id);

  const injectors: Injector[] = [getAngularAppInjector(), getLeafInjector()].filter(
    (injector): injector is Injector => injector != null,
  );

  if (injectors.length === 0) {
    console.warn('[language-bridge] setAngularLanguage chamado sem Angular montado; ignorando.');
    return;
  }

  // Cada TranslateService distinto recebe a troca uma única vez, associado ao
  // NgZone do seu injector para re-entrar na zona ao aplicar o idioma.
  const services = new Map<TranslateService, NgZone | null>();
  for (const injector of injectors) {
    const translate = injector.get(TranslateService, null);
    if (translate && !services.has(translate)) {
      services.set(translate, injector.get(NgZone, null));
    }
  }

  if (services.size === 0) {
    console.warn('[language-bridge] TranslateService indisponível nos injectors Angular montados; ignorando.');
    return;
  }

  services.forEach((zone, translate) => applyLanguageToService(translate, resolved, zone));
}
